import { clsx } from 'clsx'
import React from 'react'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-medium',
      'transition-normal',
      'focus:outline-none',
      'focus:ring-2',
      'cursor-pointer',
    ]

    const variantClasses = {
      primary: [
        'bg-gradient-brand',
        'text-primary',
        'hover:opacity-80',
        'hover:scale-105',
      ],
      secondary: ['bg-surface', 'text-primary', 'border', 'hover:opacity-80'],
      outline: ['bg-transparent', 'text-primary', 'border', 'hover:bg-surface'],
      ghost: [
        'bg-transparent',
        'text-secondary',
        'hover:text-primary',
        'hover:bg-surface',
      ],
    }

    const sizeClasses = {
      sm: ['text-sm', 'p-2', 'rounded-md'],
      md: ['text-base', 'p-3', 'rounded-lg'],
      lg: ['text-lg', 'p-4', 'rounded-xl'],
    }

    const disabledClasses =
      disabled || isLoading ? ['cursor-not-allowed', 'opacity-50'] : []

    const buttonClasses = clsx(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      disabledClasses,
      className
    )

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
